import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { hasNativePlugin } from './NativeBiometricAuthService'

/**
 * TEMPORARY MIGRATION CODE - REMOVE ONCE OLD NATIVE BUILDS HAVE MIGRATED.
 *
 * Android v14 and older, and the iOS builds before the one named below, shipped
 * before the permanent native force-update checker existed (PlayStoreVersionChecker
 * on Android, AppStoreVersionChecker on iOS). Native code cannot be added to an
 * already-installed build retroactively, but those apps do load the latest web
 * bundle - so the web layer stands in for the missing native gate until enough
 * installs have moved on.
 *
 * This is deliberately separate from the native force-update implementations and
 * must not grow into a general-purpose version check. Delete this file, the
 * component under components/LegacyNativeVersionGate, and its mount in App.js
 * when the old builds are no longer in the field.
 */

export const ANDROID_MIN_SUPPORTED_BUILD = 16

export const IOS_MIN_SUPPORTED_BUILD = 98

export const ANDROID_STORE_URL =
  'https://play.google.com/store/apps/details?id=org.orgarise.sso.twa'
export const IOS_STORE_URL = 'https://apps.apple.com/us/app/orgarise/id1589484746'

// Android v14 and older shipped as a Bubblewrap TWA - Chrome opening the site,
// with no Capacitor bridge at all. Every check below sees a plain browser there,
// so those installs would never be gated. Chrome sets this referrer when it opens
// the page from the app, and it is the only signal the TWA gives us. The Capacitor
// Android build loads server.url directly and never carries it, even though it
// ships under the same package name.
// Plugins present in every shell built after the native force-update landed, and
// in none of the shells this gate has to stop. Verified against the Podfiles:
// iOS 1.4.4 (83/84) ships only Capacitor, CapacitorCordova and CapacitorStatusBar,
// which is why StatusBar is deliberately NOT listed - old and new both carry it,
// so it discriminates nothing.
//
// Reading what the native projects already ship means nothing has to be added to
// them for this gate to work, and nothing has to be removed from them later in a
// particular order. The only standing rule is that every release keeps at least
// one of these, which holds unless push, biometric and preferences are all
// dropped in a single change.
const MODERN_SHELL_PLUGINS = [
  'App',
  'Preferences',
  'FirebaseMessaging',
  'BiometricAuthNative',
  'SecureStorage',
]

// The Capacitor Android build raised minSdkVersion to 24 (Android 7.0); the v14
// TWA it replaces allowed 19 (Android 4.4). Devices between those two run the old
// app perfectly well but CANNOT install the new one - Play filters it out. Telling
// them to update would strand them on a screen they can never leave, so they are
// let through instead. This mirrors what the native gates already do: iOS checks
// the App Store's minimumOsVersion via OSCompatibilityGate, and Android leaves the
// decision to Play's own compatibility filtering.
const ANDROID_MIN_OS_FOR_NEW_BUILD = 7

// The iOS deployment target of the build users are being sent to. Today this
// equals the old build's target (15.0), so nobody is stranded - every device that
// runs 1.4.4 can install the replacement. It is checked anyway because raising
// IPHONEOS_DEPLOYMENT_TARGET in a future release would otherwise strand everyone
// below the new floor on a screen they can never leave, and nothing in the web
// layer would notice. KEEP IN SYNC with IPHONEOS_DEPLOYMENT_TARGET.
const IOS_MIN_OS_FOR_NEW_BUILD = 15

/**
 * Major Android version from the user agent, or null when it cannot be read.
 */
function androidMajorVersion() {
  try {
    const match = /Android\s+(\d+)/.exec(navigator.userAgent || '')
    return match ? Number.parseInt(match[1], 10) : null
  } catch (e) {
    return null
  }
}

/**
 * Major iOS version from the user agent ("CPU iPhone OS 18_7" -> 18), or null.
 */
function iosMajorVersion() {
  try {
    const match = /\bOS (\d+)[_ ]/.exec(navigator.userAgent || '')
    return match ? Number.parseInt(match[1], 10) : null
  } catch (e) {
    return null
  }
}

/**
 * True only when the device is confirmed too old to install the build we would
 * be sending the user to. Such a user must never be blocked: the update screen
 * would be a dead end, since the store will refuse the install.
 *
 * A version that cannot be read returns false - "not confirmed stranded" - so the
 * user is still prompted. Failing to prompt everyone whose user agent is unusual
 * would quietly defeat the gate, and both platforms report their version
 * reliably.
 */
function cannotInstallNewBuild(platformName) {
  if (platformName === 'android') {
    const version = androidMajorVersion()
    return version !== null && version < ANDROID_MIN_OS_FOR_NEW_BUILD
  }

  if (platformName === 'ios') {
    const version = iosMajorVersion()
    return version !== null && version < IOS_MIN_OS_FOR_NEW_BUILD
  }

  return false
}

const ANDROID_TWA_REFERRER = 'android-app://org.orgarise.sso.twa'

const PLATFORMS = {
  android: { minBuild: ANDROID_MIN_SUPPORTED_BUILD, storeUrl: ANDROID_STORE_URL },
  ios: { minBuild: IOS_MIN_SUPPORTED_BUILD, storeUrl: IOS_STORE_URL },
}

const ALLOWED = { outdated: false, storeUrl: null }

/**
 * Synchronous check for a native shell too old to run this bundle.
 *
 * The shells this gate exists to stop carry none of the plugins that shipped with
 * the native force-update, and the plugin set is the signal.
 * `Capacitor.PluginHeaders` is injected by the native bridge before any app code
 * runs and reading it cannot throw or reject, so this can decide immediately.
 * The Android v14 TWA has no bridge at all and is matched separately, by the
 * referrer Chrome sets when it opens the page.
 *
 * Being synchronous matters: the gate can refuse on the very first render, so the
 * app's own code never gets a chance to run on a shell that cannot survive it.
 *
 * Requires nothing new in the native projects: it keys off plugins the current
 * builds already ship (see MODERN_SHELL_PLUGINS).
 *
 * @returns `{ outdated, storeUrl }` when the shell is too old, otherwise null.
 *          Null on anything uncertain - a browser, an unknown platform, or a
 *          bridge that advertises no plugin headers at all.
 */
export function detectLegacyShellSync() {
  try {
    // Checked before anything Capacitor-based: the old TWA has no bridge, so
    // isNativePlatform() reports false there and would let it through.
    if (typeof document !== 'undefined' &&
      typeof document.referrer === 'string' &&
      document.referrer.startsWith(ANDROID_TWA_REFERRER)) {
      // Only prompt someone who can actually install the replacement.
      if (cannotInstallNewBuild('android')) return null

      return { outdated: true, storeUrl: ANDROID_STORE_URL }
    }

    if (!Capacitor.isNativePlatform()) return null

    const platform = PLATFORMS[Capacitor.getPlatform()]
    if (!platform) return null

    const headers = Capacitor.PluginHeaders
    // No headers at all means the bridge has not reported yet: too little to go
    // on, so let the app through rather than risk blocking a working build.
    if (!Array.isArray(headers) || !headers.length) return null

    // Any one of these is proof of a current shell. Several are matched rather
    // than one so that dropping a single plugin from a future build cannot make
    // that build gate itself, which a single-marker rule would do.
    if (MODERN_SHELL_PLUGINS.some((name) => hasNativePlugin(name))) return null

    // Same rule as the TWA branch: never send someone to a store listing that
    // will refuse to install on their device.
    if (cannotInstallNewBuild(Capacitor.getPlatform())) return null

    return { outdated: true, storeUrl: platform.storeUrl }
  } catch (e) {
    return null
  }
}

/**
 * Async wrapper kept for the component's effect path.
 *
 * There is nothing asynchronous left to do: detection is entirely synchronous -
 * plugin set, TWA referrer and OS floor - which is also why the gate can refuse on
 * the very first render.
 */
export async function checkLegacyNativeVersion() {
  try {
    if (!Capacitor.isNativePlatform()) return ALLOWED

    const platform = PLATFORMS[Capacitor.getPlatform()]
    if (!platform) return ALLOWED

    const legacyShell = detectLegacyShellSync()
    if (legacyShell) return legacyShell

    // Degrades to "current build" when the plugin is not in the binary, so a
    // shell without @capacitor/app is never blocked by a check it cannot answer.
    if (!hasNativePlugin('App')) return ALLOWED

    const info = await App.getInfo()
    // `build` is the native build number as a string: versionCode on Android,
    // CURRENT_PROJECT_VERSION on iOS.
    const build = Number.parseInt(info?.build, 10)
    if (!Number.isFinite(build)) return ALLOWED

    if (build >= platform.minBuild) return ALLOWED

    return { outdated: true, storeUrl: platform.storeUrl }
  } catch (e) {
    return ALLOWED
  }
}
